import React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Label } from '@lightspeed/flame/FormField';
import { Input } from '@lightspeed/flame/Input';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Select } from '@lightspeed/flame/Select';
import { Text } from '@lightspeed/flame/Text';
import axios from 'axios';

import { createReactRenderer } from 'helpers/react-renderer';
import { AccountStatus } from 'root/merchantos/account';

type ImpersonationButtonProps = {
  accountId: number;
  level: number;
  userId?: number;
  accountStatus: AccountStatus;
  accountIsReadOnly: boolean;
  radAccountId: number;
  userIsImpersonator?: boolean;
  displayInListingColumn?: boolean;
};

const disabledAccountStatuses = [
  AccountStatus.blank,
  AccountStatus.cancelled,
  AccountStatus.delete,
];

const reasons = {
  support: 'Support troubleshooting',
  onboarding: 'Onboarding Support',
  billing: 'Sales & Billing Support',
  engineering: 'Engineering Troubleshooting',
  product: 'Product & Tech Support',
  training: 'Quality Assurance and Training Purposes',
  payments: 'Payments Risk Assessment',
};

type UrlParams = {
  // eslint-disable-next-line camelcase -- URL parameter format
  cust_customer_id: number | string;
  level: number | string;
  // eslint-disable-next-line camelcase -- URL parameter format
  cust_user_id?: number | string;
  reason?: string;
  ticket?: string;
  notes?: string;
};
const ImpersonationButton = ({
  accountId,
  userId = undefined,
  level,
  accountStatus,
  accountIsReadOnly,
  radAccountId,
  userIsImpersonator = false,
  displayInListingColumn = false,
}: ImpersonationButtonProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [ticket, setTicketNumber] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [buttonTitle, setButtonTitle] = React.useState('');

  const isReadonly = level === 40;
  const levelDescription = {
    '40': 'read-only',
    '50': 'read-write',
    '60': 'read-write (admin)',
  }[level.toString()];

  React.useEffect(() => {
    if (accountId === radAccountId) {
      setIsDisabled(true);
      setButtonTitle('Cannot impersonate into the RAD master account');
      return;
    }

    if (disabledAccountStatuses.indexOf(accountStatus) !== -1) {
      setIsDisabled(true);
      setButtonTitle(`Cannot open account with status ${accountStatus}`);
      return;
    }

    if (userIsImpersonator) {
      setIsDisabled(true);
      setButtonTitle(`Cannot impersonate as another RAD user's temporary impersonation profile`);
      return;
    }

    if (!isReadonly && accountIsReadOnly) {
      setIsDisabled(true);
      setButtonTitle(`Account is in Read-Only Mode`);
      return;
    }

    setButtonTitle(`Impersonate account in ${levelDescription} mode`);
  }, [
    accountId,
    radAccountId,
    accountStatus,
    userIsImpersonator,
    isReadonly,
    accountIsReadOnly,
    levelDescription,
  ]);

  const openImpersonationTab = React.useCallback((params: UrlParams) => {
    const query = new URLSearchParams();
    query.append('form_name', 'switch_to_account');
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        query.append(key, value.toString());
      }
    });
    window.merchantos.utils.openUrl(`misc_utils.php?${query.toString()}`, 'open_account');
  }, []);

  const openModal = React.useCallback(() => {
    setIsOpen(true);
    // reset contents
    setReason('');
    setTicketNumber('');
    setNotes('');
  }, []);

  const checkPreauthorization = React.useCallback(async () => {
    const result = await axios.get(`/staff/impersonation/preauth/${accountId}`);
    const reason = result?.data?.reason;
    if (reason) {
      setReason(reason);
    }
    return !!result?.data?.preauthorized;
  }, [accountId]);

  const handleButtonClick = React.useCallback(async () => {
    const isPreauthorized = await checkPreauthorization();
    if (!isPreauthorized) {
      openModal();
      return;
    }
    openImpersonationTab({
      cust_customer_id: accountId,
      level,
      cust_user_id: userId,
    });
  }, [checkPreauthorization, openImpersonationTab, accountId, level, userId, openModal]);

  const handleSubmit = React.useCallback(
    (e) => {
      e.preventDefault();
      if (reason === '' || (ticket === '' && notes === '')) {
        return;
      }
      openImpersonationTab({
        cust_customer_id: accountId,
        cust_user_id: userId,
        level,
        reason,
        ticket,
        notes,
      });
      setIsOpen(false);
    },
    [openImpersonationTab, accountId, userId, level, reason, ticket, notes]
  );

  return (
    <>
      <Button
        onClick={() => handleButtonClick()}
        className={isReadonly ? 'select' : 'restore'}
        disabled={isDisabled}
        title={buttonTitle}
        style={{
          ...(isDisabled ? { pointerEvents: 'auto' } : {}), // show title when disabled
          ...(displayInListingColumn ? { lineHeight: 'normal', minHeight: '0' } : {}),
        }}
      >
        <>
          <i
            className={isReadonly ? 'icon-eye-open' : 'icon-signin'}
            style={{ marginRight: '0.5rem' }}
          />
          {!displayInListingColumn && (isReadonly ? 'View Account' : 'Open Account')}
        </>
      </Button>
      <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)} css={{ width: '640px' }}>
        <form onSubmit={handleSubmit}>
          <ModalHeader>Impersonate Active Merchant Account</ModalHeader>
          <ModalBody scroll>
            <Alert type="warning" mb={4} noCloseBtn={true}>
              <Text>
                You are about to access an active merchant account in{' '}
                <strong>{levelDescription}</strong> mode.
              </Text>
              <ul>
                <li key={1}>You should not access this account without good reason</li>
                <li key={2}>This access will be logged internally</li>
                <li key={3}>The merchant will be made aware that you accessed their account</li>
                {!isReadonly && <li key={4}>Do not make any unauthorized changes</li>}
              </ul>
              <Text>Proceed with caution.</Text>
            </Alert>
            <Box mb={4}>
              <Label mb={0} htmlFor="reason-input">
                Indicate the reason for this access<span style={{ color: 'red' }}>*</span>
              </Label>
              <Text mb={2}>
                <em>This value will be shared with the merchant!</em>
              </Text>
              <Select id="reason-input" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="" key={0}>
                  Select...
                </option>
                {Object.entries(reasons).map(([key, value]) => (
                  <option value={key} key={key}>
                    {value}
                  </option>
                ))}
              </Select>
            </Box>
            <Box mb={4}>
              <Label mb={0} htmlFor="ticket-input">
                Enter the reference number to a ticket with additional context
              </Label>
              <Text mb={2}>
                <em>e.g. Zendesk, Jira, Intercom, Incident.io, etc.</em>
              </Text>
              <Input
                id="ticket-input"
                value={ticket}
                onChange={(e) => setTicketNumber(e.target.value)}
                autoComplete="off"
              />
            </Box>
            <Box mb={0}>
              <Label htmlFor="notes-input">Additional notes</Label>
              <textarea
                id="notes-input"
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                style={{ minWidth: '100%', maxWidth: '100%' }}
                maxLength={1000}
                value={notes}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="space-between">
              <Button onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={reason === '' || (ticket === '' && notes === '')}
                variant="danger"
                fill={true}
              >
                Submit
              </Button>
            </Flex>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
};

export default ImpersonationButton;

const RenderedComponent = createReactRenderer({
  component: ImpersonationButton,
  selector: '[data-react-root="ImpersonationButton"]',
});

history.onChangeState(
  {
    name: ['system.views.account'],
    id: undefined,
  },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
